import React, { useState, useEffect, useCallback } from 'react';
import {
    Drawer,
    Box,
    Typography,
    TextField,
    IconButton,
    CircularProgress,
    Pagination,
    Select,
    MenuItem,
    ListSubheader,
    Button,
    Snackbar,
    Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { GT_COLORS } from '../../../../components/groups-teams/shared/constants';
import { GroupsTeamsService } from '../../../../services/groups-teams.service';
import { TeamService } from '../../../../services/team.service';
import { GroupService } from '../../../../services/groups.service';
import { Team } from '../../../../types/teams.types';

interface UnassignedUsersDrawerProps {
    open: boolean;
    onClose: () => void;
}

const PAGE_SIZE = 15;

export const UnassignedUsersDrawer: React.FC<UnassignedUsersDrawerProps> = ({ open, onClose }) => {
    const [users, setUsers] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);

    const teams = useSelector((state: RootState) => state.teams.items) as Team[];
    const groups = useSelector((state: RootState) => state.groups.items) as { id: string; name: string }[];

    const [selectedTeams, setSelectedTeams] = useState<Record<string, string>>({});
    const [assigning, setAssigning] = useState<Record<string, boolean>>({});
    const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

    const fetchUsers = useCallback(async (p: number, q: string) => {
        setLoading(true);
        const result = await GroupsTeamsService.fetchUnassignedUsers(p, PAGE_SIZE, q);
        setUsers(result.users);
        setTotal(result.total);
        setLoading(false);
    }, []);

    useEffect(() => {
        if (open) {
            setPage(0);
            setSearch('');
            setSelectedTeams({});
            fetchUsers(0, '');
            // Ensure teams and groups are loaded in the store
            GroupService.fetchAll();
            TeamService.fetchAll();
        }
    }, [open, fetchUsers]);

    useEffect(() => {
        if (open) {
            const timeout = setTimeout(() => fetchUsers(page, search), 300);
            return () => clearTimeout(timeout);
        }
    }, [search, page, open, fetchUsers]);

    const totalPages = Math.ceil(total / PAGE_SIZE);

    const getInitials = (u: any) => {
        const f = (u.firstName || '?')[0];
        const l = (u.lastName || '?')[0];
        return `${f}${l}`.toUpperCase();
    };

    const getName = (u: any) =>
        [u.firstName, u.lastName].filter(Boolean).join(' ') || 'Unknown';

    const handleAssign = async (userId: string) => {
        const teamId = selectedTeams[userId];
        if (!teamId) return;

        const user = users.find((u) => u.id === userId);
        setAssigning((prev) => ({ ...prev, [userId]: true }));
        await GroupsTeamsService.addMember(teamId, userId, (isValid, message) => {
            if (isValid) {
                setToast({ message: 'User assigned to team', severity: 'success' });
                setUsers((prev) => prev.filter((u) => u.id !== userId));
                setTotal((prev) => prev - 1);
                setSelectedTeams((prev) => {
                    const next = { ...prev };
                    delete next[userId];
                    return next;
                });
            } else {
                setToast({ message: message || 'Failed to assign user', severity: 'error' });
            }
        }, user ? { firstName: user.firstName, lastName: user.lastName, phoneNumber: user.phoneNumber, employeeId: user.employeeId } : undefined);
        setAssigning((prev) => ({ ...prev, [userId]: false }));
    };

    // Build group name lookup
    const groupNameMap = new Map(groups.map((g) => [g.id, g.name]));

    // Group teams by their parent group for the dropdown
    const groupedTeams = teams.reduce<Record<string, Team[]>>((acc, t) => {
        const groupName = (t.groupId && groupNameMap.get(t.groupId)) || 'No Group';
        if (!acc[groupName]) acc[groupName] = [];
        acc[groupName].push(t);
        return acc;
    }, {});

    const renderTeamMenuItems = () => {
        const items: React.ReactNode[] = [];
        Object.entries(groupedTeams)
            .sort(([a], [b]) => a.localeCompare(b))
            .forEach(([groupName, groupTeams]) => {
                items.push(
                    <ListSubheader key={`header-${groupName}`} sx={{ fontSize: '11px', lineHeight: '28px', color: GT_COLORS.textFaint }}>
                        {groupName}
                    </ListSubheader>
                );
                groupTeams
                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                    .forEach((t) => {
                        items.push(
                            <MenuItem key={t.id} value={t.id} sx={{ fontSize: '13px', pl: 3 }}>
                                {t.name}
                            </MenuItem>
                        );
                    });
            });
        return items;
    };

    return (
        <Drawer anchor="right" open={open} onClose={onClose}>
            <Box sx={{ width: 480, height: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* Header */}
                <Box sx={{ p: '16px 20px', borderBottom: `1px solid ${GT_COLORS.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonOffIcon sx={{ fontSize: 20, color: GT_COLORS.amber }} />
                        <Typography sx={{ fontSize: '16px', fontWeight: 600 }}>Unassigned Users</Typography>
                        <Typography sx={{ fontSize: '13px', color: GT_COLORS.textFaint, ml: 0.5 }}>({total})</Typography>
                    </Box>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>

                {/* Search */}
                <Box sx={{ px: '20px', pt: '12px', pb: '8px' }}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search by name, phone, or employee ID..."
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                        InputProps={{
                            startAdornment: <SearchIcon sx={{ mr: 1, color: GT_COLORS.textFaint, fontSize: '18px' }} />,
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { fontSize: '14px', borderRadius: '8px' } }}
                    />
                </Box>

                {/* User list */}
                <Box sx={{ flex: 1, overflow: 'auto', px: '20px', py: '8px' }}>
                    {loading && (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <CircularProgress size={24} />
                        </Box>
                    )}

                    {!loading && users.length === 0 && (
                        <Typography sx={{ textAlign: 'center', color: GT_COLORS.textFaint, fontSize: '14px', py: 4 }}>
                            {search ? 'No matching unassigned users.' : 'All users are assigned to teams.'}
                        </Typography>
                    )}

                    {!loading && users.map((user) => (
                        <Box
                            key={user.id}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                py: '8px',
                                px: '8px',
                                borderRadius: '6px',
                                '&:hover': { backgroundColor: GT_COLORS.hoverBg },
                            }}
                        >
                            <Box
                                sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: '50%',
                                    backgroundColor: GT_COLORS.amber,
                                    color: '#fff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    flexShrink: 0,
                                }}
                            >
                                {getInitials(user)}
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                                    {getName(user)}
                                </Typography>
                                <Typography sx={{ fontSize: '12px', color: GT_COLORS.textFaint }}>
                                    {[user.phoneNumber, user.employeeId && `ID: ${user.employeeId}`].filter(Boolean).join(' · ') || 'No contact info'}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                                <Select
                                    size="small"
                                    displayEmpty
                                    value={selectedTeams[user.id] || ''}
                                    onChange={(e) => setSelectedTeams((prev) => ({ ...prev, [user.id]: e.target.value as string }))}
                                    sx={{
                                        minWidth: 140,
                                        fontSize: '13px',
                                        '& .MuiSelect-select': { py: '6px' },
                                    }}
                                >
                                    <MenuItem value="" disabled sx={{ fontSize: '13px' }}>
                                        Select team...
                                    </MenuItem>
                                    {renderTeamMenuItems()}
                                </Select>
                                <Button
                                    size="small"
                                    variant="contained"
                                    disabled={!selectedTeams[user.id] || assigning[user.id]}
                                    onClick={() => handleAssign(user.id)}
                                    sx={{
                                        textTransform: 'none',
                                        fontSize: '12px',
                                        px: '12px',
                                        py: '5px',
                                        minWidth: 'auto',
                                        borderRadius: '6px',
                                        backgroundColor: GT_COLORS.primary,
                                        '&:hover': { backgroundColor: '#1565c0' },
                                    }}
                                >
                                    {assigning[user.id] ? <CircularProgress size={14} color="inherit" /> : 'Assign'}
                                </Button>
                            </Box>
                        </Box>
                    ))}
                </Box>

                {/* Pagination */}
                {totalPages > 1 && (
                    <Box sx={{ p: '12px 20px', borderTop: `1px solid ${GT_COLORS.border}`, display: 'flex', justifyContent: 'center' }}>
                        <Pagination
                            count={totalPages}
                            page={page + 1}
                            onChange={(_, p) => setPage(p - 1)}
                            size="small"
                        />
                    </Box>
                )}
            </Box>

            <Snackbar
                open={!!toast}
                autoHideDuration={3000}
                onClose={() => setToast(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                {toast ? (
                    <Alert severity={toast.severity} onClose={() => setToast(null)} sx={{ width: '100%' }}>
                        {toast.message}
                    </Alert>
                ) : undefined}
            </Snackbar>
        </Drawer>
    );
};
